// src/services/discoveryService.js
import { apiRequest } from "./apiService";

import {
  DEFAULT_CITY_SLUG,
  DEFAULT_NICHE_SLUG,
  deriveLaunchCitySlug,
  deriveLaunchNicheSlug,
  findLaunchNicheBySlug,
} from "../data/slugIndex";

const DEFAULT_PLATFORM = "instagram";

const LAUNCH_NICHE_MODE_BY_SLUG = {
  food_reviewers: "child",
  coffee_brunch: "child",
  salon: "child",
  beauty: "parent",
  hair_beauty: "child",
  nails_lashes: "child",
  fitness: "parent",
  local_lifestyle: "parent",
};

const toStr = (value) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim();

const lower = (value) => toStr(value).toLowerCase();

const boolish = (value) => {
  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }

  if (typeof value === "string") {
    const text = value.trim().toLowerCase();

    if (["true", "1", "yes", "y", "on"].includes(text)) return true;
    if (["false", "0", "no", "n", "off"].includes(text)) return false;
  }

  return undefined;
};

const firstArrayValue = (value) =>
  Array.isArray(value) && value.length ? value[0] : undefined;

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) {
      const first = firstArrayValue(value);
      if (toStr(first)) return first;
      continue;
    }

    if (toStr(value)) return value;
  }

  return undefined;
};

const normalizeView = (value) => {
  const view = lower(value);
  return view === "creators" || view === "trending" ? view : "feed";
};

const normalizeNicheMode = (value) => {
  const mode = lower(value).replace("-", "_");
  return mode === "parent" || mode === "child" ? mode : "";
};

const getLaunchNicheMode = (slug, fallbackMode = "") => {
  const cleanSlug = deriveLaunchNicheSlug(slug, "");
  const niche = cleanSlug ? findLaunchNicheBySlug(cleanSlug) : null;

  const mode = normalizeNicheMode(
    niche?.mode || LAUNCH_NICHE_MODE_BY_SLUG[cleanSlug] || fallbackMode
  );

  return mode || "child";
};

const normalizeTags = (values) => {
  if (!Array.isArray(values)) return [];

  return Array.from(
    new Set(
      values
        .map((value) =>
          String(value ?? "")
            .trim()
            .toLowerCase()
            .replace(/^#+/, "")
            .replace(/\s+/g, "_")
        )
        .filter(Boolean)
    )
  );
};

const normalizeDiscoveryRequest = (params = {}) => {
  const feedDefaults = params.feedDefaults || params.feed_defaults || {};

  const cityRaw = firstNonEmpty(
    params.city,
    params.citySlug,
    params.city_slug,
    params.locationSlug,
    params.location_slug,
    params.location,
    params.selectedCity?.slug,
    params.citySelection?.slug,
    firstArrayValue(params.cities),
    feedDefaults.city,
    feedDefaults.citySlug,
    feedDefaults.city_slug
  );

  const nicheRaw = firstNonEmpty(
    params.niche,
    params.nicheSlug,
    params.niche_slug,
    params.preferredNicheSlug,
    params.preferred_niche_slug,
    params.selectedNiche?.slug,
    params.nicheSelection?.slug,
    firstArrayValue(params.niches),
    params.interests,
    params.industry,
    feedDefaults.niche,
    feedDefaults.nicheSlug,
    feedDefaults.niche_slug
  );

  const city = deriveLaunchCitySlug(cityRaw, DEFAULT_CITY_SLUG);
  const niche = deriveLaunchNicheSlug(nicheRaw, DEFAULT_NICHE_SLUG);

  const requestedMode = firstNonEmpty(
    params.nicheMode,
    params.niche_mode,
    params.mode,
    params.selectedNiche?.mode,
    params.nicheSelection?.mode,
    feedDefaults.nicheMode,
    feedDefaults.niche_mode
  );

  // Launch source of truth wins. Example: fitness => parent.
  const nicheMode = getLaunchNicheMode(niche, requestedMode);

  return {
    ...params,

    // Launch inventory is Instagram-only right now.
    platform: DEFAULT_PLATFORM,

    // Must match enabled launch city slug.
    city,

    // Must match enabled launch niche slug.
    niche,

    // Must match launch niche mode. Do not hardcode child.
    nicheMode,
    niche_mode: nicheMode,

    view: normalizeView(params.view),
    excludeSeen: boolish(params.excludeSeen ?? params.exclude_seen),
    exclude_seen: boolish(params.excludeSeen ?? params.exclude_seen),
  };
};

export const getDiscoveryFeed = (params = {}) => {
  const safeParams = normalizeDiscoveryRequest(params);
  const qs = new URLSearchParams();

  const platform = toStr(safeParams.platform);
  const city = toStr(safeParams.city);
  const niche = toStr(safeParams.niche);
  const nicheMode = normalizeNicheMode(
    safeParams.nicheMode || safeParams.niche_mode
  );
  const seed = toStr(safeParams.seed);
  const view = normalizeView(safeParams.view);

  if (platform) qs.set("platform", platform);
  if (city) qs.set("city", city);
  if (niche) qs.set("niche", niche);

  if (nicheMode) {
    qs.set("niche_mode", nicheMode);
  }

  if (seed) qs.set("seed", seed);
  if (view) qs.set("view", view);

  const excludeSeen = boolish(safeParams.exclude_seen ?? safeParams.excludeSeen);
  if (excludeSeen !== undefined) {
    qs.set("exclude_seen", excludeSeen ? "true" : "false");
  }

  const tags = normalizeTags(safeParams.tags ?? safeParams.selectedTags);
  if (tags.length) {
    qs.set("tags", tags.join(","));
  }

  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiRequest(`/discovery/feed${suffix}`, "GET");
};